import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Calendar, DollarSign, Package, CheckCircle, Clock, 
  XCircle, FileText, CreditCard, Printer, Plus, TrendingUp, AlertCircle
} from 'lucide-react';

import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import invoiceService from '../services/invoiceService';
import { printInvoiceDocument } from '../utils/printUtils';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado del formulario de abono
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentNote, setPaymentNote] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getInvoiceById(id);
      setInvoice(response.invoice);
    } catch (error) {
      showNotification('Error al cargar la factura', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    const numericAmount = parseInt(String(paymentAmount).replace(/[^\d]/g, ''));
    if (!numericAmount || numericAmount <= 0) {
      return showNotification('Ingresa un monto válido mayor a 0', 'error');
    }
    try {
      setSubmittingPayment(true);
      await invoiceService.addPayment(id, {
        amount: numericAmount,
        method: paymentMethod,
        note: paymentNote
      });
      showNotification('✅ Abono registrado exitosamente', 'success');
      setPaymentAmount('');
      setPaymentNote('');
      setShowPaymentModal(false);
      loadInvoice();
    } catch (error) {
      showNotification(error.message || 'Error al registrar abono', 'error');
    } finally {
      setSubmittingPayment(false);
    }
  };

  useEffect(() => { if (id) loadInvoice(); }, [id]);

  // ========== HELPERS ==========
  const getStatusBadge = (status) => {
    const map = {
      'DRAFT':     { label: 'Borrador',   style: { backgroundColor: '#F3F4F6', color: '#4B5563', border: '1px solid #D1D5DB' } },
      'SENT':      { label: 'Enviada',    style: { backgroundColor: '#DBEAFE', color: '#2563EB', border: '1px solid #93C5FD' } },
      'PAID':      { label: 'Pagada',     style: { backgroundColor: '#D1FAE5', color: '#059669', border: '1px solid #6EE7B7' } },
      'OVERDUE':   { label: 'Vencida',    style: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } },
      'CANCELLED': { label: 'Cancelada',  style: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } },
    };
    return map[status] || map['DRAFT'];
  };

  const getPaymentStatusBadge = (status) => {
    const map = {
      'PENDING':  { label: 'Pendiente',    style: { backgroundColor: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D' } },
      'PAID':     { label: 'Pagado',       style: { backgroundColor: '#D1FAE5', color: '#059669', border: '1px solid #6EE7B7' } },
      'PARTIAL':  { label: 'Pago Parcial', style: { backgroundColor: '#DBEAFE', color: '#2563EB', border: '1px solid #93C5FD' } },
      'OVERDUE':  { label: 'Vencido',      style: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } },
      'REFUNDED': { label: 'Reembolsado',  style: { backgroundColor: '#EDE9FE', color: '#7C3AED', border: '1px solid #C4B5FD' } },
    };
    return map[status] || map['PENDING'];
  };

  const methodLabels = { CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia', CHECK: 'Cheque', OTHER: 'Otro' };

  const fmt = (n) => (n || 0).toLocaleString('en-US');

  // ========== CÁLCULOS DEL PLAN ==========
  const calcPhases = () => {
    if (!invoice) return [];
    
    const plan = invoice.paymentPlan || { type: '100%', details: { initialPaymentPercent: 100, installments: 0 } };
    let pct = plan.details?.initialPaymentPercent;
    let inst = plan.details?.installments;
    
    // Deduce values from string type if details are missing or defaulted to 100 wrongly
    if ((!pct || pct === 100) && plan.type) {
      if (plan.type === '50/50') { pct = 50; inst = 1; }
      else if (plan.type === '40/30/30') { pct = 40; inst = 2; }
      else if (plan.type === '40/40/20') { pct = 40; inst = 2; }
      else if (plan.type === 'CUSTOM') { pct = pct || 100; inst = inst || 0; }
      else { pct = 100; inst = 0; }
    }
    
    // Safety fallbacks
    pct = pct || 100;
    inst = inst || 0;

    const initialAmount = Math.round((invoice.total * pct) / 100);
    const remainingTotal = invoice.total - initialAmount;

    const phases = [];
    
    // Fase inicial
    const initialPaid = (invoice.payments || []).filter(p => p.phase === 'INITIAL').reduce((s, p) => s + p.amount, 0);
    phases.push({
      key: 'INITIAL',
      label: `Pago Inicial (${pct}%)`,
      expected: initialAmount,
      paid: initialPaid,
      progress: initialAmount > 0 ? Math.min(100, Math.round((initialPaid / initialAmount) * 100)) : 100,
      color: '#F59E0B'
    });

    // Cuotas
    if (pct < 100 && inst > 0) {
      // Determine if we have a mapped uneven distribution
      let percentages = [];
      if (plan.type === '40/40/20') percentages = [40, 20];
      else if (plan.type === '40/30/30') percentages = [30, 30];
      else if (plan.type === '50/50') percentages = [50];
      else {
        // Fallback to equal splits
        const splitPct = (100 - pct) / inst;
        for (let i=0; i<inst; i++) percentages.push(splitPct);
      }

      let accumulatedBase = 0;
      for (let i = 1; i <= inst; i++) {
        const phaseKey = `INSTALLMENT_${i}`;
        
        let expected = 0;
        if (percentages[i-1]) {
           expected = Math.round((invoice.total * percentages[i-1]) / 100);
        } else {
           // Fallback base
           expected = Math.round(remainingTotal / inst);
        }
        
        // Make sure the last installment rounds out exactly to the total
        if (i === inst) {
           expected = remainingTotal - accumulatedBase;
        }
        accumulatedBase += expected;

        const paid = (invoice.payments || []).filter(p => p.phase === phaseKey).reduce((s, p) => s + p.amount, 0);
        phases.push({
          key: phaseKey,
          label: `Cuota ${i}`,
          expected,
          paid,
          progress: expected > 0 ? Math.min(100, Math.round((paid / expected) * 100)) : 100,
          color: '#3B82F6'
        });
      }
    }
    return phases;
  };

  // ========== RENDER ==========
  if (loading) return (
    <div className="animate-fadeIn">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: '#6B7280' }}>Cargando factura...</p>
      </div>
    </div>
  );

  if (!invoice) return (
    <div className="animate-fadeIn">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>❌</div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Factura no encontrada</h3>
        <button onClick={() => navigate('/invoices')} className="btn btn-primary"><ArrowLeft size={16} /> Volver</button>
      </div>
    </div>
  );

  const phases = calcPhases();
  const totalPaid = invoice.totalPaid || 0;
  const totalProgress = invoice.total > 0 ? Math.min(100, Math.round((totalPaid / invoice.total) * 100)) : 0;
  const statusBadge = getStatusBadge(invoice.status);
  const paymentBadge = getPaymentStatusBadge(invoice.paymentStatus);
  const isPaid = invoice.paymentStatus === 'PAID';

  return (
    <div className="animate-fadeIn">
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/invoices')} className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={20} /> Volver
          </button>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: '800', color: '#111827', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText size={28} style={{ color: '#059669' }} />
              {invoice.invoiceNumber}
            </h1>
            <p style={{ color: '#6B7280', marginTop: '2px', fontSize: '0.875rem' }}>Dashboard de factura</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '600', ...statusBadge.style }}>
            {statusBadge.label}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '600', ...paymentBadge.style }}>
            {paymentBadge.label}
          </span>
          <button onClick={() => printInvoiceDocument(invoice)} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700' }}>
            <Printer size={16} /> Imprimir / PDF
          </button>
        </div>
      </div>

      {/* LAYOUT 2 COLUMNAS */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        
        {/* ====== COLUMNA IZQUIERDA (2/3) ====== */}
        <div style={{ flex: '2 1 500px', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>

          {/* Servicios Facturados */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Package size={20} style={{ color: '#059669' }} />
                Servicios Facturados ({invoice.items?.length || 0})
              </h3>
            </div>
            <div className="card-content">
              {invoice.items?.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: idx < invoice.items.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <div>
                    <h4 style={{ fontWeight: '600', color: '#111827', margin: 0 }}>
                      {item.service?.name || item.description || 'Producto Personalizado'}
                    </h4>
                    <p style={{ fontSize: '0.8rem', color: '#6B7280', margin: '2px 0 0' }}>
                      {item.service?.description || item.description || ''}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>{item.quantity} × ${fmt(item.unitPrice)}</span>
                    <div style={{ fontWeight: '700', color: '#059669', fontSize: '1rem' }}>${fmt(item.totalPrice)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ====== PLAN DE PAGOS — BARRAS DE PROGRESO ====== */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <TrendingUp size={20} style={{ color: '#F59E0B' }} />
                Plan de Pagos: {invoice.paymentPlan?.type === 'CUSTOM' ? 'Cuotas Personalizadas' : (invoice.paymentPlan?.type || '100%')}
              </h3>
              {!isPaid && (
                <button 
                  onClick={() => setShowPaymentModal(true)} 
                  className="btn btn-primary btn-sm" 
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', padding: '6px 18px' }}
                >
                  <Plus size={16} /> Registrar Pago
                </button>
              )}
            </div>
            <div className="card-content" style={{ padding: '20px' }}>
              {/* Barra de progreso TOTAL */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontWeight: '700', color: '#111827', fontSize: '0.9rem' }}>Progreso Total</span>
                  <span style={{ fontWeight: '800', color: totalProgress >= 100 ? '#059669' : '#F59E0B', fontSize: '1rem' }}>{totalProgress}%</span>
                </div>
                <div style={{ height: '14px', backgroundColor: '#E5E7EB', borderRadius: '9999px', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${totalProgress}%`, 
                    background: totalProgress >= 100 ? 'linear-gradient(90deg, #10B981, #059669)' : 'linear-gradient(90deg, #F59E0B, #EF4444)',
                    borderRadius: '9999px', 
                    transition: 'width 0.8s ease-in-out',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.75rem', color: '#6B7280' }}>
                  <span>Abonado: ${fmt(totalPaid)}</span>
                  <span>Total: ${fmt(invoice.total)}</span>
                </div>
              </div>

              {/* Barra por cada FASE */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {phases.map((phase) => {
                  const isComplete = phase.progress >= 100;
                  const barColor = isComplete ? '#10B981' : phase.color;
                  return (
                    <div key={phase.key} style={{ padding: '14px 16px', backgroundColor: isComplete ? '#F0FDF4' : '#F9FAFB', borderRadius: '12px', border: `1px solid ${isComplete ? '#BBF7D0' : '#E5E7EB'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isComplete ? <CheckCircle size={16} style={{ color: '#10B981' }} /> : <Clock size={16} style={{ color: '#9CA3AF' }} />}
                          <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>{phase.label}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontWeight: '700', color: isComplete ? '#059669' : '#374151', fontSize: '0.95rem' }}>
                            ${fmt(phase.paid)} <span style={{ fontWeight: '400', color: '#9CA3AF', fontSize: '0.8rem' }}>/ ${fmt(phase.expected)}</span>
                          </span>
                        </div>
                      </div>
                      <div style={{ height: '8px', backgroundColor: '#E5E7EB', borderRadius: '9999px', overflow: 'hidden' }}>
                        <div style={{ 
                          height: '100%', width: `${phase.progress}%`, 
                          backgroundColor: barColor, borderRadius: '9999px', 
                          transition: 'width 0.6s ease-in-out' 
                        }}></div>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: '0.7rem', color: '#9CA3AF', marginTop: '4px' }}>
                        {phase.progress}% cubierto {phase.expected - phase.paid > 0 && !isComplete ? `· Falta: $${fmt(phase.expected - phase.paid)}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ====== HISTORIAL DE ABONOS ====== */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CreditCard size={20} style={{ color: '#6366F1' }} />
                Historial de Abonos ({invoice.payments?.length || 0})
              </h3>
            </div>
            <div className="card-content" style={{ padding: 0 }}>
              {(!invoice.payments || invoice.payments.length === 0) ? (
                <div style={{ padding: '32px', textAlign: 'center', color: '#9CA3AF' }}>
                  <DollarSign size={36} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <p style={{ fontSize: '0.9rem' }}>Aún no se han registrado abonos</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Fecha</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Monto</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Método</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Fase</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#374151' }}>{new Date(p.date).toLocaleDateString()} {new Date(p.date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</td>
                        <td style={{ padding: '10px 16px', fontWeight: '700', color: '#059669' }}>${fmt(p.amount)}</td>
                        <td style={{ padding: '10px 16px', color: '#6B7280' }}>{methodLabels[p.method] || p.method}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ 
                            display: 'inline-block', padding: '2px 10px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '600',
                            backgroundColor: p.phase === 'INITIAL' ? '#FEF3C7' : p.phase?.startsWith('INSTALLMENT') ? '#DBEAFE' : '#F3F4F6',
                            color: p.phase === 'INITIAL' ? '#D97706' : p.phase?.startsWith('INSTALLMENT') ? '#2563EB' : '#6B7280'
                          }}>
                            {p.phase === 'INITIAL' ? 'Inicial' : p.phase?.replace('INSTALLMENT_', 'Cuota ') || 'General'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', color: '#9CA3AF', fontSize: '0.8rem' }}>{p.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>


          {/* Notas */}
          {invoice.notes && (
            <div className="card">
              <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={20} style={{ color: '#8B5CF6' }} /> Notas</h3></div>
              <div className="card-content"><p style={{ color: '#4B5563', whiteSpace: 'pre-wrap' }}>{invoice.notes}</p></div>
            </div>
          )}
        </div>

        {/* ====== COLUMNA DERECHA (1/3) ====== */}
        <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: '300px', maxWidth: '420px' }}>

          {/* TOTALIZADOR MONUMENTAL */}
          <div className="card" style={{ background: 'linear-gradient(135deg, #065F46 0%, #047857 50%, #059669 100%)', color: 'white', border: 'none' }}>
            <div className="card-content" style={{ padding: '28px', textAlign: 'center' }}>
              <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.8, marginBottom: '4px' }}>Total Factura</p>
              <div style={{ fontSize: '2.5rem', fontWeight: '900', letterSpacing: '-1px' }}>${fmt(invoice.total)}</div>
              <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.2)', margin: '16px auto', width: '60%' }}></div>
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                <div>
                  <p style={{ fontSize: '0.65rem', textTransform: 'uppercase', opacity: 0.7 }}>Abonado</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: '800' }}>${fmt(totalPaid)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.65rem', textTransform: 'uppercase', opacity: 0.7 }}>Pendiente</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: '800', color: isPaid ? '#A7F3D0' : '#FCD34D' }}>${fmt(Math.max(0, invoice.total - totalPaid))}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Info del Cliente */}
          <div className="card">
            <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><User size={20} style={{ color: '#3B82F6' }} /> Cliente</h3></div>
            <div className="card-content" style={{ padding: '16px' }}>
              <p style={{ fontWeight: '700', color: '#111827', fontSize: '1rem', margin: 0 }}>{invoice.client?.name || 'N/A'}</p>
              <p style={{ color: '#6B7280', fontSize: '0.85rem', margin: '4px 0 0' }}>{invoice.client?.email || ''}</p>
              <p style={{ color: '#6B7280', fontSize: '0.85rem', margin: '2px 0 0' }}>{invoice.client?.phone || ''}</p>
            </div>
          </div>

          {/* Fechas */}
          <div className="card">
            <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Calendar size={20} style={{ color: '#6366F1' }} /> Fechas</h3></div>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Emisión:</span>
                <span style={{ fontWeight: '600', color: '#111827', fontSize: '0.85rem' }}>{new Date(invoice.issueDate).toLocaleDateString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Vencimiento:</span>
                <span style={{ fontWeight: '600', color: '#111827', fontSize: '0.85rem' }}>{new Date(invoice.dueDate).toLocaleDateString()}</span>
              </div>
              {invoice.paidDate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Pagada:</span>
                  <span style={{ fontWeight: '600', color: '#059669', fontSize: '0.85rem' }}>{new Date(invoice.paidDate).toLocaleDateString()}</span>
                </div>
              )}
              {invoice.billingPeriod && (invoice.billingPeriod.startDate || invoice.billingPeriod.endDate) && (
                <div style={{ borderTop: '1px solid #E5E7EB', marginTop: '12px', paddingTop: '12px' }}>
                  <p style={{ fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Periodo de Facturación</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span>{invoice.billingPeriod.startDate ? new Date(invoice.billingPeriod.startDate).toLocaleDateString() : '—'}</span>
                    <span style={{ color: '#9CA3AF' }}>→</span>
                    <span>{invoice.billingPeriod.endDate ? new Date(invoice.billingPeriod.endDate).toLocaleDateString() : '—'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resumen Financiero */}
          <div className="card">
            <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><DollarSign size={20} style={{ color: '#059669' }} /> Resumen Financiero</h3></div>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280' }}>Subtotal:</span>
                <span style={{ fontWeight: '500' }}>${fmt(invoice.subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280' }}>Impuestos ({invoice.taxRate || 0}%):</span>
                <span style={{ fontWeight: '500' }}>${fmt(invoice.tax)}</span>
              </div>
              <div style={{ borderTop: '2px solid #E5E7EB', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '700', color: '#111827', fontSize: '1.1rem' }}>Total:</span>
                <span style={{ fontWeight: '800', color: '#059669', fontSize: '1.1rem' }}>${fmt(invoice.total)}</span>
              </div>
            </div>
          </div>

          {/* Venta Asociada */}
          <div className="card">
            <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Package size={20} style={{ color: '#3B82F6' }} /> Venta Asociada</h3></div>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>ID Venta:</span>
                <span style={{ fontWeight: '600', fontSize: '0.85rem' }}>#{invoice.sale?._id?.slice(-6) || 'N/A'}</span>
              </div>
              <button onClick={() => navigate(`/sales/${invoice.sale?._id}`)} className="btn btn-outline btn-sm" style={{ width: '100%', marginTop: '8px' }}>
                Ver Detalle de Venta
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ====== MODAL DE REGISTRAR PAGO ====== */}
      {showPaymentModal && (
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowPaymentModal(false); }}
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            zIndex: 9999, padding: '20px' 
          }}
        >
          <div style={{ 
            backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out'
          }}>
            {/* Header */}
            <div style={{ 
              padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #065F46, #059669)', borderRadius: '16px 16px 0 0', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CreditCard size={22} />
                <span style={{ fontSize: '1.1rem', fontWeight: '700' }}>Registrar Abono</span>
              </div>
              <button 
                onClick={() => setShowPaymentModal(false)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.4rem', padding: '0 4px', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              {/* Sugerencia con botón Elegir */}
              {phases.length > 0 && (() => {
                const nextPhase = phases.find(p => p.progress < 100);
                if (!nextPhase) return null;
                const pending = nextPhase.expected - nextPhase.paid;
                return (
                  <div style={{ 
                    marginBottom: '20px', padding: '12px 16px', backgroundColor: '#FEF3C7', 
                    borderRadius: '10px', fontSize: '0.85rem', color: '#92400E', 
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                    border: '1px solid #FCD34D'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <AlertCircle size={18} style={{ flexShrink: 0 }} />
                      <span>Próximo pago sugerido: <strong>${fmt(pending)}</strong> para completar "{nextPhase.label}"</span>
                    </div>
                    <button 
                      onClick={() => setPaymentAmount(pending.toLocaleString('en-US'))}
                      style={{ 
                        backgroundColor: '#D97706', color: 'white', border: 'none', 
                        padding: '6px 16px', borderRadius: '8px', fontWeight: '700', 
                        fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#B45309'}
                      onMouseLeave={e => e.target.style.backgroundColor = '#D97706'}
                    >
                      Elegir
                    </button>
                  </div>
                );
              })()}

              {/* Monto */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#374151', marginBottom: '6px' }}>Monto del Abono *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#6B7280', fontWeight: 'bold', fontSize: '1.1rem' }}>$</span>
                  <input
                    type="text"
                    value={paymentAmount}
                    onChange={e => {
                      let raw = e.target.value.replace(/[^\d]/g, '');
                      setPaymentAmount(raw ? parseInt(raw).toLocaleString('en-US') : '');
                    }}
                    placeholder="0"
                    className="form-input"
                    style={{ paddingLeft: '32px', textAlign: 'right', fontSize: '1.5rem', fontWeight: '800', width: '100%', height: '56px', letterSpacing: '0.5px' }}
                    autoFocus
                  />
                </div>
              </div>

              {/* Método de pago */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#374151', marginBottom: '6px' }}>Método de Pago</label>
                <select
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value)}
                  className="form-input"
                  style={{ width: '100%' }}
                >
                  <option value="CASH">💵 Efectivo</option>
                  <option value="CARD">💳 Tarjeta</option>
                  <option value="TRANSFER">🏦 Transferencia</option>
                  <option value="CHECK">📄 Cheque</option>
                  <option value="OTHER">📋 Otro</option>
                </select>
              </div>

              {/* Nota */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#374151', marginBottom: '6px' }}>Nota (Opcional)</label>
                <input
                  type="text"
                  value={paymentNote}
                  onChange={e => setPaymentNote(e.target.value)}
                  placeholder="Ej: Abono con tarjeta débito del cliente"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </div>

              {/* Botones */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="btn btn-outline"
                  style={{ flex: 1, height: '48px', fontWeight: '600' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddPayment}
                  disabled={submittingPayment || !paymentAmount}
                  className="btn btn-primary"
                  style={{ flex: 2, height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '700', fontSize: '1rem' }}
                >
                  {submittingPayment ? (
                    <>
                      <div className="loading-spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }}></div>
                      Registrando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} /> Confirmar Abono
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default InvoiceDetailPage;
